import {
    BlankModal,
    HStack,
    ModalBody,
    ModalFooter,
    ModalHeader,
    useToast,
    VStack,
    Button,
    IconButton,
} from '@cad-arc';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { useFieldArray, useForm } from 'react-hook-form';
import { FormSelect } from '../forms/integrated-form-controls/form-select';
import { useEventSearchBootstrap } from '../event-search/use-event-search-bootstrap';
import React from 'react';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { useApiClient } from '~/api-client';
import { useMutation } from '@tanstack/react-query';
import {
    ApiResult,
    CadSearchsaveResource,
    ShareEventSearchForm,
    ShareEventSearchFormType,
} from '@mark43/cad-api';
import { AxiosError } from 'axios-cad-nx';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';

export type ShareSavedSearchModalComponentType = typeof ShareSavedSearchModal;

export interface ShareSavedSearchModalProps {
    savedSearchId: number;
    savedSearchName: string;
    shareEventSearchForm?: ShareEventSearchForm;
    onSuccess?: () => void;
}

const defaultEntityPermissionOption: ShareEventSearchFormType['entityPermissions'][number] = {
    operationTypeOption: {
        label: '',
        value: 0,
    },
    roleOption: {
        label: '',
        value: 0,
    },
};

const defaultShareForm: ShareEventSearchFormType = {
    entityPermissions: [defaultEntityPermissionOption],
};

export const ShareSavedSearchModal = ({
    savedSearchId,
    savedSearchName,
    shareEventSearchForm,
    onSuccess,
}: ShareSavedSearchModalProps) => {
    const apiClient = useApiClient();
    const modalControls = useFullscreenModalControls();
    const bootstrap = useEventSearchBootstrap();
    const toast = useToast();

    const shareSearchForm = useForm<ShareEventSearchFormType>({
        mode: 'onBlur',
        defaultValues: shareEventSearchForm ? shareEventSearchForm : defaultShareForm,
    });

    const { fields, append, remove } = useFieldArray({
        control: shareSearchForm.control,
        name: 'entityPermissions',
    });

    const updateSavedEventSearchMutation = useMutation({
        mutationFn: (form: ShareEventSearchFormType) => {
            return apiClient.request<CadSearchsaveResource.UpdateEventSearchSharingSettings>({
                method: 'PUT',
                url: 'cad_search/save/event/share',
                data: {
                    savedSearchId,
                    isShared: form.entityPermissions.length > 0,
                    shareEventSearchForm: form as ShareEventSearchForm,
                },
            });
        },
        onError: (e: AxiosError<ApiResult>) => {
            toast({
                description: e?.response?.data?.error,
                status: 'error',
            });
        },
        onSuccess: () => {
            onSuccess?.();
            modalControls.close();
        },
    });
    const currentUserRoleId = useBootstrapState().userProfileView.userRoleId;

    return (
        <IntegratedFormProvider<ShareEventSearchFormType>
            form={shareSearchForm}
            mutation={updateSavedEventSearchMutation}
        >
            <BlankModal isOpen onClose={modalControls.close}>
                <ModalHeader>Share Search {savedSearchName}</ModalHeader>
                <ModalBody>
                    <VStack alignItems="flex-start" gap={2}>
                        {fields.map((field, index) => {
                            /**
                             * Searches, when shared, are automatically shared with the user themselves. The user
                             * cannot change this, so there's no reason to show it in the list anyway.
                             */
                            if (currentUserRoleId === field?.roleOption?.value) {
                                return null;
                            }

                            return (
                                <HStack key={field.id} w="100%" gap={2} alignItems="flex-end">
                                    <FormSelect
                                        options={
                                            bootstrap.data.eventSearchSharingOptions
                                                .departmentRoleOptions
                                        }
                                        name={`entityPermissions.${index}.roleOption`}
                                        label="Role"
                                        control={shareSearchForm.control}
                                    />
                                    <FormSelect
                                        options={
                                            bootstrap.data.eventSearchSharingOptions
                                                .operationTypeOptions
                                        }
                                        name={`entityPermissions.${index}.operationTypeOption`}
                                        label="Permission"
                                        control={shareSearchForm.control}
                                    />
                                    {/* Account for hidden user role entry */}
                                    {fields.length - 1 > 1 ? (
                                        <IconButton icon="TrashCan" onClick={() => remove(index)} />
                                    ) : null}
                                </HStack>
                            );
                        })}
                        <Button
                            variant="ghost"
                            leadingVisual="Add"
                            onClick={() => append(defaultEntityPermissionOption)}
                        >
                            Add Role
                        </Button>
                    </VStack>
                </ModalBody>

                <ModalFooter>
                    <HStack w="100%" justifyContent="flex-end">
                        <FormCancelButton onClick={modalControls.close} />
                        <FormSaveButton>Share Search</FormSaveButton>
                    </HStack>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
};
